import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import './SessionReadOnlyModal.scss';
import { Tooltip } from 'react-tooltip';
import { useGetSessionByIdQuery } from '../coachingActionSlice';
import { metricValueFormat } from '../../../utils/metricValueFormat';
// import TargetArrow from '../../../assets/targetArrow.svg';
// import Ascending from '../../../assets/ascending.png';
// import Descending from '../../../assets/descending.png';
import NotAchievedIcon from '../../../assets/SadFace.png';
import CompletedIcon from '../../../assets/Smileface.png';
import { FollowUpResultProgress } from '../followUpResultProgress/FollowUpResultProgress';
import { FollowUpPeriodResult } from '../followUpPeriodResult/FollowUpPeriodResult';
import { useFollowupPeriodResultQuery } from '../coachingActionPlanSlice';
import GradientDefs from '../../../components/metricMonthlySummaryCard/GradientDefs';
import { MetricMonthlySummaryCard } from '../../../components/metricMonthlySummaryCard/MetricMonthlySummaryCard';
import { CoachingTabs } from '../../../utils/typeConstants';
import { isTieredScorecard } from '../../../utils/metricAttainment';

interface SessionReadOnlyModalProps {
    sessionId: number;
    sessionTitle: string;
    employeeAvatarUrl?: string;
    CAPSessionData?: any;
    onClose: () => void;
}

type SectionKey = 'analyze' | 'coach' | 'results';

const PrintIcon: React.FC<{ onClick: () => void }> = ({ onClick }) => (
    <button
        data-testid='printButton'
        onClick={onClick}
        title="Print Session"
        className="custom-print-icon-btn"
    >
        <i className="pi pi-print" />
    </button>
);

export const SessionReadOnlyModal: React.FC<SessionReadOnlyModalProps> = ({
    sessionId,
    sessionTitle,
    employeeAvatarUrl,
    CAPSessionData,
    onClose,
}) => {
    const [expandedSections, setExpandedSections] = useState<Record<SectionKey, boolean>>({
        analyze: true,
        coach: true,
        results: true,
    });

    const [dailyData, setDailyData] = useState<any>(null);

    const handlePrint = () => {
        setExpandedSections({
            analyze: true,
            coach: true,
            results: true,
        });

        setTimeout(() => {
            window.print();
        }, 500);
    };

    const { data, isLoading } = useGetSessionByIdQuery(sessionId, {
        skip: !sessionId || sessionId <= 0,
    });
    const session = data?.data;

    // ── Find the clicked session from CAPSessionData ──────────────────────────
    const matchedSession = CAPSessionData?.sessions?.find(
        (s: any) => s.coachingActionPlanSessionId === sessionId
    );

    // ── Fetch period results using clicked session's own dates ────────────────
    const allSessions = CAPSessionData?.sessions ?? [];
    const latestSession = allSessions.length > 0
        ? allSessions.reduce((max: any, s: any) => s.sessionNumber > max.sessionNumber ? s : max)
        : null;
    const isLatestSession = matchedSession?.coachingActionPlanSessionId === latestSession?.coachingActionPlanSessionId;
    const isCapClosed = CAPSessionData?.coachingActionPlanStatusId === 3;
    const latestSessionToDate = isCapClosed && CAPSessionData?.coachingCompleteDate
        ? CAPSessionData.coachingCompleteDate
        : `${(new Date().getMonth() + 1).toString().padStart(2, '0')}/${new Date().getDate().toString().padStart(2, '0')}/${new Date().getFullYear()} 00:00:00`;

    const periodToDate = isLatestSession ? latestSessionToDate : matchedSession?.followUpDate;

    const { data: FollowupPeriod } = useFollowupPeriodResultQuery({
        employeeNumber: CAPSessionData?.employeeNumber,
        scorecardVersionId: CAPSessionData?.scorecardVersionId,
        metricId: CAPSessionData?.metricId,
        fromDate: matchedSession?.sessionStartDate,
        toDate: periodToDate,
    }, {
        skip: !matchedSession?.sessionStartDate || !periodToDate,
    });

    useEffect(() => {
        if (FollowupPeriod?.data && FollowupPeriod.data.length > 0) {
            setDailyData(FollowupPeriod.data);
        }
    }, [FollowupPeriod]);

    // ── Derived values ────────────────────────────────────────────────────────
    const metricTypeName = CAPSessionData?.metricTypeName;
    // const isMetricAscending = matchedSession?.metricDirectionId === 1;
    const metricCurrentValue = matchedSession?.metricCurrentValue;
    const metricTargetValue = matchedSession?.metricTargetValue;
    const commitmentAttained = matchedSession?.sessionCommitmentAttained ?? false;
    const isCoachingStarted = matchedSession?.sessionNumber === 0;

    // performanceCommitment:
    // - Decimal:    stored as value/100 (0.98 → 0.0098) → multiply * 100 to display
    // - Percentage: stored as-is (50 → 50)              → display as-is
    const performanceCommitment = matchedSession?.performanceCommitment;

    // const metricAttained = matchedSession?.sessionCommitmentAttained ?? false;

    // Use metricResultValue for progress arc if available, else metricCurrentValue
    // const progressValue = matchedSession?.metricResultValue ?? metricCurrentValue;
    // const progressPercent =
    //     progressValue !== undefined && metricTargetValue && metricTargetValue > 0
    //         ? Math.min(100, Math.round((progressValue / metricTargetValue) * 100))
    //         : 0;

    // const radius = 45;
    // const circumference = 2 * Math.PI * radius;
    // const strokeDashoffset = circumference - (progressPercent / 100) * circumference;

    // ── Helpers ───────────────────────────────────────────────────────────────
    const toggleSection = (key: SectionKey) => {
        setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        const day = date.getDate();
        const suffixes = ['th', 'st', 'nd', 'rd'];
        const v = day % 100;
        const suffix = suffixes[(v - 20) % 10] ?? suffixes[v] ?? suffixes[0];
        const month = date.toLocaleDateString('en-US', { month: 'long' });
        const year = date.getFullYear();
        return `${month} ${day}${suffix}, ${year}`;
    };

    const getInitials = (name?: string): string => {
        if (!name) return '??';
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getStatusIcon = (attained: boolean) => attained ? CompletedIcon : NotAchievedIcon;

    // ── Header ────────────────────────────────────────────────────────────────
    const header = (
        <div className="session-modal__header" id="SessionReadOnlyModal">
            <div className="session-modal__header-avatar">
                {employeeAvatarUrl ? (
                    <img src={employeeAvatarUrl} alt={session?.employee_name ?? 'Employee'} />
                ) : (
                    <div className="session-modal__header-avatar-initials">
                        {CAPSessionData?.shortNameAlt || getInitials(session?.employee_name)}
                    </div>
                )}
            </div>
            <div className="session-modal__header-info">
                <p className="session-modal__header-date">
                    Information Date: {formatDate(matchedSession?.sessionStartDate ?? session?.session_start_date)}
                </p>
                <p className="session-modal__header-session">{sessionTitle}</p>
                <p className="session-modal__header-name">
                    {session?.employee_name ?? CAPSessionData?.employeeName ?? '—'}
                </p>
                <p className="session-modal__header-metric">
                    Metric Coached: {CAPSessionData?.label ?? session?.coaching_skill_name ?? '—'}
                </p>
            </div>
        </div>
    );



    return (
        <Dialog
            visible
            onHide={onClose}
            header={header}
            className="session-modal"
            icons={<PrintIcon onClick={handlePrint} />}
            style={{ width: '900px', maxHeight: '90vh' }}
            contentStyle={{ maxHeight: '75vh', overflowY: 'auto' }}
            modal
            draggable={false}
            resizable={false}
        >
            {isLoading && (
                <div className="session-modal__loading">
                    <i className="pi pi-spin pi-spinner" /> Loading session details...
                </div>
            )}

            {!isLoading && !session && (
                <div className="session-modal__empty">No session data found.</div>
            )}

            {!isLoading && session && (
                <div className="session-modal__body" id="SessionReadOnlyModal">

                    {/* ── CLOSED CAP SUMMARY ──────────────────────────────── */}
                    {CAPSessionData?.coachingActionPlanStatusId === 3 && (
                        <div className="session-modal__closed-summary">
                            <div className="session-modal__closed-summary-item">
                                <p className="session-modal__field-label">Final Metric Value</p>
                                <p className="session-modal__field-value session-modal__field-value--highlight">
                                    {metricValueFormat(CAPSessionData?.metricFinalValue, metricTypeName)}
                                </p>
                            </div>
                            <div className="session-modal__closed-summary-item">
                                <p className="session-modal__field-label">Coaching End Date</p>
                                <p className="session-modal__field-value">
                                    {formatDate(CAPSessionData?.coachingCompleteDate)}
                                </p>
                            </div>
                            {CAPSessionData?.metricFinalValueOverride != null && (
                                <div className="session-modal__closed-summary-item session-modal__closed-summary-item--override">
                                    <i
                                        className="pi pi-info-circle"
                                        data-tooltip-id={`cap-final-override-${sessionId}`}
                                        style={{
                                            cursor: 'pointer',
                                            fontSize: '22px',
                                            color: '#C0392B',
                                            backgroundColor: '#FADBD8',
                                            borderRadius: '50%',
                                            padding: '4px',
                                        }}
                                    />
                                    <Tooltip
                                        id={`cap-final-override-${sessionId}`}
                                        className="rounded tooltip override-tooltip"
                                        events={['click']}
                                        place="right-start"
                                        closeOnEsc
                                        positionStrategy="fixed"
                                        style={{ overflow: 'visible', maxHeight: 'none', zIndex: 99999 }}
                                    >
                                        <div className="override-tooltip__content">
                                            <span className="override-tooltip__label">Overridden Final Value</span>
                                            <span className="override-tooltip__value">
                                                {metricValueFormat(CAPSessionData.metricFinalValueOverride, metricTypeName)}
                                            </span>
                                            <span className="override-tooltip__description">
                                                {CAPSessionData.metricFinalValueOverrideReason ?? ''}
                                            </span>
                                        </div>
                                    </Tooltip>
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── ANALYZE ─────────────────────────────────────────── */}
                    <section className="session-modal__section">
                        <button
                            className="session-modal__section-toggle"
                            onClick={() => toggleSection('analyze')}
                        >
                            <span>Analyze</span>
                            <i className={`pi ${expandedSections.analyze ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
                        </button>
                        <GradientDefs />
                        {expandedSections.analyze && (
                            <div className="review-adjust">
                                {/* Row 1: Metric Card + Analysis */}
                                <div className="review-adjust__row">
                                    <div className="review-adjust__metric">
                                        <MetricMonthlySummaryCard
                                            targetVal={matchedSession?.metricTargetValue}
                                            metricAttained={matchedSession?.sessionCommitmentAttained ?? false}
                                            metricNameVal={CAPSessionData?.label}
                                            metricTypeName={CAPSessionData?.metricTypeName}
                                            metricVal={matchedSession?.metricCurrentValue}
                                            metricDesc={CAPSessionData?.metricDescription}
                                            metricDirectionId={matchedSession?.metricDirectionId}
                                            onCardClick={() => { }}
                                            selectedMetric={CAPSessionData?.label}
                                            index={CAPSessionData?.metricIndex ?? 0}
                                            metricOmitted={false}
                                            via={CoachingTabs.CoachingHistory}
                                            CAPCurrentSession={matchedSession}
                                            metricTypeId={CAPSessionData?.metricTypeId}
                                            overrideInfo={
                                                matchedSession?.metricCurrentValueOverride != null
                                                    ? {
                                                        label: 'Overridden Data',
                                                        value: metricValueFormat(
                                                            matchedSession.metricCurrentValueOverride,
                                                            CAPSessionData?.metricTypeName
                                                        ),
                                                        description: matchedSession.metricCurrentValueOverrideReason ?? '',
                                                    }
                                                    : undefined
                                            }
                                            // onBeforeOverride={onBeforeOverride}
                                            variant={isTieredScorecard(CAPSessionData?.scorecardCalculationRuleId) ? 'tiered' : 'legacy'}
                                            attainmentRank={matchedSession?.metricResultValueAttainmentRank}
                                            attainmentLevelLabel={matchedSession?.metricResultValueAttainmentLevelLabel}
                                            targets={CAPSessionData?.scorecardCalculationRuleId === 2 ? matchedSession?.targets : matchedSession?.metricTargetValue ?? CAPSessionData?.target}
                                            min={CAPSessionData?.min}
                                            max={CAPSessionData?.max}
                                        />
                                    </div>

                                    <div className="session-modal__analysis-notes">
                                        <p className="session-modal__field-label">
                                            Analysis for {session.employee_name}
                                        </p>
                                        <p className="session-modal__field-value">
                                            {matchedSession?.analysisNotes ?? session.analysis_notes ?? '—'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ── COACH ────────────────────────────────────────────── */}
                    <section className="session-modal__section">
                        <button
                            className="session-modal__section-toggle"
                            onClick={() => toggleSection('coach')}
                        >
                            <span>Coach</span>
                            <i className={`pi ${expandedSections.coach ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
                        </button>

                        {expandedSections.coach && (
                            <div className="session-modal__section-body">
                                <div className="session-modal__fuel-notes">
                                    <div className="session-modal__field">
                                        <p className="session-modal__field-label">Frame Notes</p>
                                        <p className="session-modal__field-value">
                                            {matchedSession?.frameNotes ?? session.frame_notes ?? '—'}
                                        </p>
                                    </div>
                                    <div className="session-modal__field">
                                        <p className="session-modal__field-label">Understanding Notes</p>
                                        <p className="session-modal__field-value">
                                            {matchedSession?.understandNotes ?? session.understand_notes ?? '—'}
                                        </p>
                                    </div>
                                    <div className="session-modal__field">
                                        <p className="session-modal__field-label">Exploration Notes</p>
                                        <p className="session-modal__field-value">
                                            {matchedSession?.exploreNotes ?? session.explore_notes ?? '—'}
                                        </p>
                                    </div>
                                </div>

                                <div className="session-modal__layout-header">
                                    <p className="session-modal__layout-title">Lay Out: Action Plan</p>
                                    <p className="session-modal__layout-subtitle">
                                        Create the concrete action plan with all details – If you do not change data, it will remain the same.
                                    </p>
                                </div>

                                <div className="session-modal__layout-row">
                                    <div className="session-modal__layout-cell session-modal__layout-cell--metric">
                                        <p className="session-modal__field-label">
                                            {CAPSessionData?.label ?? session.coaching_skill_name ?? 'Metric'}
                                        </p>
                                        <p className="session-modal__metric-values">
                                            <span className="session-modal__current-value">
                                                {metricValueFormat(metricCurrentValue, metricTypeName)}
                                            </span>
                                            <span className="session-modal__target-value">
                                                {' / '}{metricValueFormat(metricTargetValue, metricTypeName)}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="session-modal__layout-cell">
                                        <p className="session-modal__field-label">Performance Commitment</p>
                                        <p className="session-modal__field-value session-modal__field-value--highlight">
                                            {performanceCommitment != null
                                                ? metricValueFormat(performanceCommitment, metricTypeName)
                                                : '—'}
                                        </p>
                                        <p className="session-modal__field-hint">Type: {metricTypeName}</p>
                                    </div>
                                    <div className="session-modal__layout-cell">
                                        <p className="session-modal__field-label">Follow Up</p>
                                        <p className="session-modal__field-value">
                                            {formatDate(matchedSession?.followUpDate ?? session.follow_up_date)}
                                        </p>
                                    </div>
                                </div>

                                <div className="session-modal__layout-row session-modal__layout-row--sba">
                                    <div className="session-modal__layout-cell">
                                        <p className="session-modal__field-label">Skill</p>
                                        <p className="session-modal__field-value">
                                            {session.coaching_skill_name || '—'}
                                        </p>
                                    </div>
                                    <div className="session-modal__layout-cell">
                                        <p className="session-modal__field-label">Behavior</p>
                                        <p className="session-modal__field-value">
                                            {session.coaching_behavior_name || '—'}
                                        </p>
                                    </div>
                                    <div className="session-modal__layout-cell">
                                        <p className="session-modal__field-label">Action</p>
                                        <p className="session-modal__field-value">
                                            {session.coaching_action_name || '—'}
                                        </p>
                                    </div>
                                </div>

                                <div className="session-modal__field">
                                    <p className="session-modal__field-label">Action Description</p>
                                    <p className="session-modal__field-value">
                                        {matchedSession?.actionDescription ?? session.action_description ?? '—'}
                                    </p>
                                </div>

                                <div className="session-modal__layout-row session-modal__layout-row--criteria">
                                    <div className="session-modal__layout-cell">
                                        <p className="session-modal__field-label">Success Criteria</p>
                                        <p className="session-modal__field-value">
                                            {matchedSession?.successCriteria ?? session.success_criteria ?? '—'}
                                        </p>
                                    </div>
                                    <div className="session-modal__layout-cell">
                                        <p className="session-modal__field-label">Notes</p>
                                        <p className="session-modal__field-value">
                                            {matchedSession?.notes ?? session.notes ?? '—'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* ── RESULTS ──────────────────────────────────────────── */}
                    <section className="session-modal__section">
                        <button
                            className="session-modal__section-toggle"
                            onClick={() => toggleSection('results')}
                        >
                            <span>Results</span>
                            <i className={`pi ${expandedSections.results ? 'pi-chevron-up' : 'pi-chevron-down'}`} />
                        </button>

                        {expandedSections.results && (
                            <div className="session-modal__section-body">
                                {isCoachingStarted ? (
                                    <p className="session-modal__no-results">No Results yet</p>
                                ) : (
                                    <>
                                        {CAPSessionData?.sessions && CAPSessionData.sessions.length > 0 && (
                                            <div className="session-modal__progress-section">
                                                <FollowUpResultProgress
                                                    CAPSession={CAPSessionData}
                                                    metricIndex={CAPSessionData.metricIndex || 0}
                                                />
                                            </div>
                                        )}

                                        <div className="session-modal__results-grid">
                                            <div className="session-modal__results-metric">
                                                <p className="session-modal__results-label">Current Performance</p>
                                                <p className="session-modal__results-value">
                                                    {metricValueFormat(metricCurrentValue, metricTypeName)}
                                                </p>
                                            </div>
                                            <div className="session-modal__results-metric">
                                                <p className="session-modal__results-label">Performance Commitment</p>
                                                <p className="session-modal__results-value">
                                                    {metricValueFormat(performanceCommitment, metricTypeName)}
                                                </p>
                                            </div>
                                            <div className="session-modal__results-metric">
                                                <p className="session-modal__results-label">Scorecard Target</p>
                                                <p className="session-modal__results-value">
                                                    {metricValueFormat(metricTargetValue, metricTypeName)}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="session-modal__results-row">
                                            <div className="session-modal__status-section">
                                                <div className="session-modal__status-header">
                                                    <p className="session-modal__status-title">Status</p>
                                                </div>
                                                <div className="session-modal__status-content">
                                                    <img
                                                        src={getStatusIcon(commitmentAttained)}
                                                        alt="Status"
                                                        className="session-modal__status-icon"
                                                    />
                                                    <div className="session-modal__status-info">
                                                        <p className={`session-modal__status-text ${commitmentAttained
                                                                ? 'session-modal__status-text--success'
                                                                : 'session-modal__status-text--danger'
                                                            }`}>
                                                            {commitmentAttained ? 'Metric Attained' : 'Metric Not Attained'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="session-modal__period-section">
                                                <div className="session-modal__period-header">
                                                    <p className="session-modal__period-title">Period Results</p>
                                                </div>
                                                <div className="session-modal__period-content">
                                                    <FollowUpPeriodResult
                                                        metricIndex={CAPSessionData?.metricIndex || 0}
                                                        dailyData={dailyData}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </section>

                </div>
            )}
        </Dialog>
    );
};